/**
 * Data layer for `custom-fx-rate-per-invoice`. Thin typed fetch wrappers over /api/v1/custom-fx-rate-per-invoice.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomFxRatePerInvoiceRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomFxRatePerInvoiceResponse {
	count: number;
	items: CustomFxRatePerInvoiceRow[];
}

export interface CreateCustomFxRatePerInvoiceBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-fx-rate-per-invoice";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomFxRatePerInvoiceResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomFxRatePerInvoiceResponse>(res);
}

export async function create(body: CreateCustomFxRatePerInvoiceBody): Promise<CustomFxRatePerInvoiceRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomFxRatePerInvoiceRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customFxRatePerInvoiceQueryKeys = {
	all: ["custom-fx-rate-per-invoice"] as const,
	list: () => [...customFxRatePerInvoiceQueryKeys.all] as const,
	detail: (id: string) => [...customFxRatePerInvoiceQueryKeys.all, id] as const,
};
